<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { RouterLink, RouterView, useRoute } from 'vue-router'
import { storeToRefs } from 'pinia'
import ThemeToggle from './components/ThemeToggle.vue'
import BrandLogo from '@/components/BrandLogo.vue'
import { formatRemaining, minutesRemaining } from '@/lib/impersonation'
import { useAuthStore } from '@/stores/auth'
import { useThemeStore } from '@/stores/theme'

// Initialize theme BEFORE the layout paints. `<script setup>` runs
// synchronously during component setup, so the `.dark` class is on
// `<html>` before the first render — no light→dark flash on reload.
const themeStore = useThemeStore()
themeStore.init()

const route = useRoute()
const authStore = useAuthStore()
const {
  email,
  isAuthenticated,
  tenantSuspended,
  canAdminister,
  isPlatformAdmin,
  isImpersonating,
  impersonatedTenantName,
  impersonationExpiresAt,
} = storeToRefs(authStore)

// ---------- The impersonation banner ----------

/**
 * 🔴 WHY THIS SITS IN THE LAYOUT AND NOT ON THE PANEL. Once a window is open the
 * operator is looking at ordinary customer screens — a pond, a chart, an alert —
 * and every one of them looks exactly as it does for the customer. The banner is
 * the ONLY thing telling them whose data is on screen, so it has to survive
 * every navigation, which means living above `<RouterView/>` rather than inside
 * any view. The panel that started the window is itself unreachable by then.
 */
const now = ref(new Date())
let clock: ReturnType<typeof setInterval> | null = null

// Ticks only while a window is open. A permanent 30-second timer would wake the
// app up for every signed-in user forever to render nothing.
watch(
  isImpersonating,
  (open) => {
    if (open && clock === null) {
      now.value = new Date()
      clock = setInterval(() => {
        now.value = new Date()
      }, 30_000)
    } else if (!open && clock !== null) {
      clearInterval(clock)
      clock = null
    }
  },
  { immediate: true },
)

onBeforeUnmount(() => {
  if (clock !== null) clearInterval(clock)
})

const remaining = computed(() =>
  formatRemaining(minutesRemaining(impersonationExpiresAt.value, now.value)),
)

/**
 * The window ran out while the operator was looking at it. Re-read the session
 * so the UI catches up with a backend that already stopped impersonating —
 * otherwise the banner sits at "less than a minute left" indefinitely while the
 * next click behaves as a platform admin, which is the more confusing of the two
 * possible mistakes.
 */
watch(
  () => minutesRemaining(impersonationExpiresAt.value, now.value),
  (minutes) => {
    if (isImpersonating.value && minutes === 0) void authStore.checkSession()
  },
)

const leaving = ref(false)
const leaveError = ref<string | null>(null)

/**
 * Leave the customer's account. NOT a sign-out — see `stopImpersonation` in the
 * API client for why the two are different actions with the same effect on the
 * window and very different effects on the operator.
 */
async function onStopImpersonating(): Promise<void> {
  leaving.value = true
  leaveError.value = null
  try {
    // Ends in a full page load of the panel — the store does it, because the
    // tenant changes and every tenant-scoped store must go (see `applySession`).
    await authStore.stopImpersonating()
  } catch {
    leaveError.value = 'Could not leave. Try again.'
  } finally {
    leaving.value = false
  }
}

// Full-page (no topbar) whenever the authenticated chrome does not belong: on
// any public route (login, forgot/reset password) AND whenever no one is logged
// in.
//
// The `!isAuthenticated` clause was originally load-bearing for a different
// reason: the chrome used to mount UsageStatusBar, whose protected fetch 401s
// while logged out and made the interceptor hijack the navigation to /login, so
// /forgot-password never rendered. That bar is gone — usage and cost figures are
// platform-only now — but the clause stays, because "show the signed-in chrome
// only to someone signed in" is correct on its own and any future protected
// fetch in the topbar would reintroduce exactly that bug.
const isChromeless = computed(
  () =>
    !isAuthenticated.value ||
    route.meta['public'] === true ||
    route.name === 'login',
)

// The store ends in a full page load of /login; a router push here would only
// race it.
async function onLogout(): Promise<void> {
  await authStore.signOut()
}
</script>

<template>
  <RouterView v-if="isChromeless" />
  <div v-else class="layout">
    <header class="topbar">
      <RouterLink to="/" class="brand" aria-label="Scanway — home">
        <BrandLogo name="scanway" class="brand-scanway" />
        <span class="brand-sep" aria-hidden="true"></span>
        <BrandLogo name="hydra" class="brand-hydra" />
      </RouterLink>
      <div class="auth-controls">
        <ThemeToggle />
        <template v-if="isAuthenticated">
          <!--
            Admins and owners only. The router redirects everyone else off
            /team anyway (meta.requiresAdmin), and the API refuses them
            regardless — this hides a link that would only ever bounce.
          -->
          <RouterLink v-if="canAdminister" to="/team" class="nav-link">
            Team
          </RouterLink>
          <!--
            OURS, not a customer's. Shown on the platform axis alone — no rank
            on the role ladder produces this link, and it disappears while
            impersonating because the backend reports `is_platform_admin: false`
            for the length of the window. Same argument as the Team link above:
            the router and the API both refuse everyone else, so this hides a
            link that would only ever bounce.
          -->
          <RouterLink
            v-if="isPlatformAdmin"
            to="/admin/tenants"
            class="nav-link"
            data-testid="nav-admin-tenants"
          >
            Customers
          </RouterLink>
          <span class="auth-user">{{ email }}</span>
          <button data-testid="logout-btn" class="logout-btn" @click="onLogout">
            Log out
          </button>
        </template>
      </div>
    </header>
    <!--
      Read-only notice. Sits between the header and the content so it is on
      every screen, not just the one where a write happens to be attempted.
      The API refuses those writes anyway, with a 403 whose detail the error
      handling renders — but only after the click. This is the same sentence,
      said first.
      `role="status"`, not `alert`: it describes a standing condition of the
      account, not something that just went wrong, and an assertive live region
      would interrupt a screen reader on every navigation.
    -->
    <!--
      Whose account is on screen. `role="status"` for the same reason the
      read-only notice uses it: a standing condition, not something that just
      went wrong. Rendered ABOVE the suspension banner because "you are inside
      somebody else's account" changes the meaning of everything below it,
      including that notice.
    -->
    <div
      v-if="isImpersonating"
      class="impersonation-banner"
      role="status"
      data-testid="impersonation-banner"
    >
      <span>
        <strong>Viewing as {{ impersonatedTenantName }}.</strong>
        Everything you do here is recorded against your own account.
      </span>
      <span v-if="remaining" class="impersonation-banner__left" data-testid="impersonation-left">
        {{ remaining }}
      </span>
      <button
        type="button"
        :disabled="leaving"
        data-testid="impersonation-exit"
        @click="onStopImpersonating"
      >
        {{ leaving ? 'Leaving…' : 'Exit' }}
      </button>
      <span v-if="leaveError" role="alert" data-testid="impersonation-error">
        {{ leaveError }}
      </span>
    </div>
    <div v-if="tenantSuspended" class="suspended-banner" role="status" data-testid="suspended-banner">
      <strong>This account is read-only.</strong>
      Your existing data stays visible, but changes and new satellite fetches
      are disabled.
    </div>
    <main>
      <RouterView />
    </main>
  </div>
</template>

<style scoped>
.layout {
  display: flex;
  flex-direction: column;
  /* Use FIXED height (not min-height) so the chart's flex:1 chain has a
     definite anchor — otherwise Chart.js + maintainAspectRatio:false
     keeps growing the canvas which grows the parent which grows main… */
  height: 100vh;
  /* Reserve space for the fixed status bar (collapsed). */
  padding-bottom: 44px;
}
.topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 56px;
  padding: 0 24px;
  border-bottom: 1px solid var(--line);
  background: var(--panel-solid);
  position: sticky;
  top: 0;
  z-index: 100;
}
.brand {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  text-decoration: none;
  border-radius: 6px;
}
.brand:focus-visible {
  outline: none;
  box-shadow: var(--ring);
}
/* Scanway is the primary mark. */
.brand :deep(.brand-scanway) {
  height: 26px;
}
/* Thin separator between company and product family. */
.brand-sep {
  width: 1px;
  height: 20px;
  background: var(--line-strong);
}
/* Hydra is the secondary product mark — must read clearly smaller than
   Scanway. Its glyphs are bold and fill the full logo height, so it needs a
   noticeably smaller height than the (thin) Scanway wordmark to stay
   subordinate. */
.brand :deep(.brand-hydra) {
  height: 9px;
  opacity: 0.75;
}
.auth-controls {
  display: flex;
  align-items: center;
  gap: 12px;
}
.auth-user {
  font-size: var(--font-size-sm);
  color: var(--text-3);
}
.nav-link {
  font-size: var(--font-size-sm);
  color: var(--text-3);
  text-decoration: none;
}
.nav-link:hover,
.nav-link.router-link-active {
  color: var(--text, inherit);
  text-decoration: underline;
}
.logout-btn {
  font: inherit;
  font-size: var(--font-size-sm);
  font-weight: 500;
  padding: 6px 12px;
  background: transparent;
  color: var(--text-2);
  border: 1px solid var(--line);
  border-radius: 6px;
  cursor: pointer;
  transition:
    background-color 150ms ease,
    color 150ms ease,
    border-color 150ms ease;
}
.logout-btn:hover {
  background: var(--ctl-bg);
  color: var(--text);
  border-color: var(--line-strong);
}
.logout-btn:focus-visible {
  outline: none;
  box-shadow: var(--ring);
}
/* Amber, not red: this is a standing state of the account, not an error that
   just occurred. Red here would compete with the actual error banners the views
   render, and after a day of seeing it the operator would start ignoring both.
   Colours come from the existing custom properties so it follows the theme
   toggle; `color-mix` with `transparent` keeps it legible on light and dark
   without a second rule. */
.impersonation-banner {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.75rem;
  padding: 0.6rem 1.2rem;
  background: color-mix(in srgb, var(--accent-violet, #6b4fbb) 18%, transparent);
  border-bottom: 1px solid color-mix(in srgb, var(--accent-violet, #6b4fbb) 45%, transparent);
  color: var(--text);
  font-size: var(--font-size-base);
  line-height: 1.45;
}

.impersonation-banner__left {
  margin-left: auto;
  opacity: 0.85;
  font-variant-numeric: tabular-nums;
}

.suspended-banner {
  padding: 0.6rem 1.2rem;
  background: color-mix(in srgb, var(--watch, #c08a2e) 16%, transparent);
  border-bottom: 1px solid color-mix(in srgb, var(--watch, #c08a2e) 45%, transparent);
  color: var(--text);
  font-size: var(--font-size-base);
  line-height: 1.45;
}

main {
  flex: 1;
  display: flex;
  min-height: 0;
  overflow: hidden;
}
</style>
